<script>
import liffService from "@/services/liff";
import { drawVipSpin, getVipSpinStatus } from "@/services/vipSpin";

const SPIN_DURATION_MS = 5200;
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 12;
const DEFAULT_SLOTS = [
  100,
  150,
  200,
  100,
  250,
  150,
  100,
  300,
  200,
  100,
  250,
  150
];

export default {
  name: "VipGoldenSpin",
  data() {
    return {
      state: "loading",
      accessToken: "",
      status: null,
      claim: null,
      slots: DEFAULT_SLOTS,
      rotation: 0,
      spinning: false,
      detailMessage: "",
      pollAttempts: 0,
      pollTimer: null,
      spinTimer: null,
      destroyed: false
    };
  },
  computed: {
    profile() {
      return liffService.profile || {};
    },
    displayName() {
      return this.profile.displayName || "สมาชิก UFAEASY888";
    },
    avatarStyle() {
      return this.profile.pictureUrl
        ? { backgroundImage: `url(${this.profile.pictureUrl})` }
        : {};
    },
    minDeposit() {
      return Number((this.status && this.status.minDeposit) || 5000);
    },
    qualifyTotal() {
      return Number((this.status && this.status.qualifyTotal) || 0);
    },
    prize() {
      return Number((this.claim && this.claim.prize) || 0);
    },
    formattedQualifyTotal() {
      return this.qualifyTotal.toLocaleString();
    },
    formattedMinDeposit() {
      return this.minDeposit.toLocaleString();
    },
    segmentAngle() {
      return 360 / this.slots.length;
    },
    // --- wheel geometry, viewBox 0 0 500 500 ---
    wheelGeom() {
      return { cx: 250, cy: 250, rPie: 208, rBulb: 227, rLabel: 172 };
    },
    // Pie slices drawn from the centre, clockwise from 12 o'clock.
    wheelSegments() {
      const { cx, cy, rPie } = this.wheelGeom;
      const point = (deg, r) => {
        const rad = (deg * Math.PI) / 180;
        return [cx + r * Math.sin(rad), cy - r * Math.cos(rad)];
      };
      return this.slots.map((prize, index) => {
        const startDeg = index * this.segmentAngle;
        const [x1, y1] = point(startDeg, rPie);
        const [x2, y2] = point(startDeg + this.segmentAngle, rPie);
        return {
          key: `slot-${index}`,
          d: `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(
            2
          )} A ${rPie} ${rPie} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`,
          fill:
            prize === 300
              ? "url(#vsSegTop)"
              : index % 2 === 0
              ? "url(#vsSegA)"
              : "url(#vsSegB)",
          sepX: x1.toFixed(2),
          sepY: y1.toFixed(2)
        };
      });
    },
    // Tangential labels with no flip. The wheel stops at rotation -mid(winner), so the winning
    // label's net on-screen rotation is exactly 0: the prize you won always reads upright under
    // the pointer. The rest tilt with the wheel, like a real prize wheel.
    wheelLabels() {
      const { cx, cy, rLabel } = this.wheelGeom;
      return this.slots.map((prize, index) => {
        const mid = index * this.segmentAngle + this.segmentAngle / 2;
        const rad = (mid * Math.PI) / 180;
        const x = cx + rLabel * Math.sin(rad);
        const y = cy - rLabel * Math.cos(rad);
        return {
          key: `label-${index}`,
          text: String(prize),
          x: x.toFixed(2),
          y: y.toFixed(2),
          rotate: mid.toFixed(1),
          top: prize === 300
        };
      });
    },
    wheelBulbs() {
      const { cx, cy, rBulb } = this.wheelGeom;
      const count = this.slots.length * 2;
      return Array.from({ length: count }, (unused, index) => {
        const rad =
          ((index * (360 / count) + this.segmentAngle / 4) * Math.PI) / 180;
        return {
          key: `bulb-${index}`,
          cx: (cx + rBulb * Math.sin(rad)).toFixed(2),
          cy: (cy - rBulb * Math.cos(rad)).toFixed(2),
          fill: index % 2 === 0 ? "url(#vsBulbOn)" : "url(#vsBulbOff)"
        };
      });
    },
    wheelStyle() {
      return {
        transform: `rotate(${this.rotation}deg)`,
        transition: this.spinning
          ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
          : "none"
      };
    },
    canSpin() {
      return this.state === "ready";
    },
    card() {
      const cards = {
        "not-qualified": {
          variant: "warning",
          title: "ยังไม่เข้าเงื่อนไข",
          message: `สิทธิ์นี้สำหรับสมาชิกที่มียอดฝากรวมเดือนสิงหาคมตั้งแต่ ${this.formattedMinDeposit} บาทขึ้นไป ยอดของคุณคือ ${this.formattedQualifyTotal} บาท`
        },
        "phone-already-claimed": {
          variant: "warning",
          title: "เบอร์นี้ใช้สิทธิ์ไปแล้ว",
          message:
            "เบอร์โทรที่ผูกกับบัญชีนี้หมุนวงล้อ VIP ไปแล้วกับอีกบัญชี รับได้คนละ 1 ครั้งเท่านั้น"
        },
        "event-upcoming": {
          variant: "info",
          title: "ยังไม่ถึงเวลา",
          message: "วงล้อ VIP เปิดให้หมุนวันที่ 7–10 กันยายน 2026"
        },
        "event-closed": {
          variant: "warning",
          title: "หมดเวลาแล้ว",
          message: "วงล้อ VIP เปิดให้หมุนเฉพาะวันที่ 7–10 กันยายน 2026 เท่านั้น"
        },
        crediting: {
          variant: "info",
          title: `คุณได้ ${this.prize} เครดิต!`,
          message: "ระบบกำลังเติมเครดิตเข้าบัญชี โดยปกติไม่เกิน 1–2 นาที"
        },
        credited: {
          variant: "success",
          title: `คุณได้ ${this.prize} เครดิต!`,
          message:
            "เติมเข้าบัญชีเรียบร้อยแล้ว ขอบคุณที่เป็นลูกค้าคนพิเศษของเรานะคะ"
        },
        "credit-failed": {
          variant: "error",
          title: `คุณได้ ${this.prize} เครดิต`,
          message:
            "บันทึกผลไว้แล้ว แต่ระบบเติมเครดิตขัดข้อง กรุณาแจ้งแอดมินพร้อมรหัสสมาชิกเพื่อรับเครดิต"
        },
        "member-error": {
          variant: "error",
          title: "ตรวจสอบสมาชิกไม่ได้",
          message:
            this.detailMessage ||
            "ไม่พบบัญชี UFAEASY888 ที่ผูกกับ LINE นี้ กรุณาติดต่อแอดมิน"
        },
        "session-error": {
          variant: "error",
          title: "กรุณาเปิดลิงก์ใหม่",
          message:
            "LIFF session หมดอายุ กรุณาปิดหน้านี้แล้วเปิดลิงก์จาก LINE อีกครั้ง"
        },
        error: {
          variant: "error",
          title: "เกิดข้อผิดพลาด",
          message: this.detailMessage || "กรุณาลองใหม่อีกครั้ง"
        }
      };
      return cards[this.state] || null;
    },
    showRetry() {
      return [
        "event-upcoming",
        "crediting",
        "credit-failed",
        "session-error",
        "error"
      ].includes(this.state);
    }
  },
  mounted() {
    document.body.classList.add("vip-spin-body");
    this.loadStatus();
  },
  beforeDestroy() {
    this.destroyed = true;
    document.body.classList.remove("vip-spin-body");
    this.stopPolling();
    if (this.spinTimer) window.clearTimeout(this.spinTimer);
  },
  methods: {
    async loadStatus() {
      this.stopPolling();
      this.state = "loading";
      this.detailMessage = "";
      this.accessToken = liffService.getAccessToken() || "";
      if (!this.accessToken) {
        this.state = "session-error";
        return;
      }
      try {
        const status = await getVipSpinStatus(this.accessToken);
        if (this.destroyed) return;
        this.applyStatus(status);
      } catch (error) {
        if (!this.destroyed) this.handleError(error, "status");
      }
    },

    applyStatus(status) {
      this.status = status;
      if (Array.isArray(status.slots) && status.slots.length) {
        this.slots = status.slots;
      }
      if (status.claim) {
        // Already spun: park the wheel on the winning slot without animating.
        this.claim = status.claim;
        this.spinning = false;
        // Park on the winning slot. rotationDeg comes from the server, which also drew the prize.
        this.rotation = Number(status.claim.rotationDeg) || 0;
        this.applyClaimState(status.claim);
        return;
      }
      if (status.eventStatus === "closed") {
        this.state = "event-closed";
      } else if (status.reason === "phone-already-claimed") {
        this.state = "phone-already-claimed";
      } else if (!status.eligible) {
        this.state = "not-qualified";
      } else if (status.eventStatus === "upcoming") {
        this.state = "event-upcoming";
      } else {
        this.state = "ready";
      }
    },

    applyClaimState(claim) {
      const creditStatus = claim.creditStatus || "pending";
      if (creditStatus === "credited") {
        this.state = "credited";
        this.stopPolling();
      } else if (
        creditStatus === "failed" ||
        creditStatus === "dispatch-failed"
      ) {
        this.state = "credit-failed";
        this.stopPolling();
      } else {
        this.state = "crediting";
      }
    },

    async spin() {
      if (!this.canSpin) return;
      this.state = "spinning";
      this.detailMessage = "";
      let result;
      try {
        result = await drawVipSpin(this.accessToken);
        if (this.destroyed) return;
      } catch (error) {
        if (!this.destroyed) this.handleError(error, "draw");
        return;
      }
      this.claim = result.claim;
      this.spinning = true;
      this.rotation = Number(result.claim.rotationDeg) || 0;
      this.spinTimer = window.setTimeout(() => {
        if (this.destroyed) return;
        this.spinning = false;
        this.applyClaimState(this.claim);
        if (this.state === "crediting") this.startPolling();
      }, SPIN_DURATION_MS);
    },

    startPolling() {
      this.stopPolling();
      this.pollAttempts = 0;
      this.pollTimer = window.setInterval(this.pollStatus, POLL_INTERVAL_MS);
    },

    stopPolling() {
      if (this.pollTimer) window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    async pollStatus() {
      if (this.destroyed || this.state !== "crediting") {
        this.stopPolling();
        return;
      }
      this.pollAttempts += 1;
      if (this.pollAttempts > POLL_MAX_ATTEMPTS) {
        this.stopPolling();
        return;
      }
      try {
        const status = await getVipSpinStatus(this.accessToken);
        if (this.destroyed || !status.claim) return;
        this.claim = status.claim;
        this.applyClaimState(status.claim);
      } catch (error) {
        // Keep polling; a manual refresh also works.
      }
    },

    handleError(error, phase) {
      const code = this.errorCode(error);
      const reason = this.errorReason(error);
      if (code === "not-found") {
        this.state = "member-error";
      } else if (code === "unauthenticated" || code === "permission-denied") {
        this.state = "session-error";
      } else if (code === "failed-precondition" && phase === "draw" && reason) {
        // The server re-checked eligibility; reload so the page shows the exact reason.
        this.loadStatus();
      } else if (code === "failed-precondition") {
        this.detailMessage =
          "พบหลายบัญชี UFAEASY888 ที่ใช้ LINE เดียวกัน กรุณาติดต่อแอดมิน";
        this.state = "member-error";
      } else {
        this.detailMessage = "การเชื่อมต่อขัดข้อง กรุณากดลองอีกครั้ง";
        this.state = "error";
      }
    },

    errorCode(error) {
      const rawCode = error && error.code ? String(error.code) : "";
      return rawCode.includes("/") ? rawCode.split("/").pop() : rawCode;
    },

    errorReason(error) {
      return error && error.details && error.details.reason
        ? String(error.details.reason)
        : "";
    },

    closePage() {
      if (liffService.isInClient) {
        liffService.closeWindow();
      } else {
        this.$router.replace("/home");
      }
    }
  }
};
</script>

<template>
  <div class="vip-spin-page">
    <div class="page-glow page-glow--one" aria-hidden="true"></div>
    <div class="page-glow page-glow--two" aria-hidden="true"></div>

    <header class="vs-header">
      <img
        class="vs-header__logo"
        src="../assets/images/logo2.png"
        alt="UFA Easy888"
      />
      <div
        class="vs-header__avatar"
        :style="avatarStyle"
        aria-hidden="true"
      ></div>
    </header>

    <main class="vs-content">
      <section class="hero">
        <span class="hero__badge">7–10 กันยายน 2026</span>
        <h1 class="hero__title">VIP GOLDEN <em>SPIN</em></h1>
        <p class="hero__sub">ลุ้นรับเครดิต 100–300 หมุนฟรี 1 ครั้ง</p>
      </section>

      <div v-if="state === 'loading'" class="loading-card" aria-live="polite">
        <span class="loading-card__spinner"></span>
        <strong>กำลังตรวจสอบสิทธิ์</strong>
        <small>กรุณารอสักครู่</small>
      </div>

      <template v-else>
        <div class="wheel-wrap">
          <div class="wheel-halo" aria-hidden="true"></div>

          <!-- Static frame: the bezel and bulbs never rotate. -->
          <svg class="wheel-layer" viewBox="0 0 500 500" aria-hidden="true">
            <defs>
              <linearGradient id="vsBezel" x1="0" y1="0" x2="0.35" y2="1">
                <stop offset="0%" stop-color="#fff8de" />
                <stop offset="14%" stop-color="#f7d97f" />
                <stop offset="32%" stop-color="#cb9a21" />
                <stop offset="48%" stop-color="#7d5a0d" />
                <stop offset="62%" stop-color="#d9ab3a" />
                <stop offset="82%" stop-color="#fbeaa6" />
                <stop offset="100%" stop-color="#9d7412" />
              </linearGradient>
              <linearGradient id="vsBezelIn" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stop-color="#fff3c4" />
                <stop offset="45%" stop-color="#d3a326" />
                <stop offset="100%" stop-color="#6b4c08" />
              </linearGradient>
              <radialGradient id="vsBulbOn" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stop-color="#fffdf2" />
                <stop offset="45%" stop-color="#ffe07a" />
                <stop offset="100%" stop-color="#e0a017" />
              </radialGradient>
              <radialGradient id="vsBulbOff" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stop-color="#fff4cf" />
                <stop offset="50%" stop-color="#c2922c" />
                <stop offset="100%" stop-color="#6b4c08" />
              </radialGradient>
              <filter
                id="vsBulbGlow"
                x="-90%"
                y="-90%"
                width="280%"
                height="280%"
              >
                <feGaussianBlur stdDeviation="4.5" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <circle cx="250" cy="250" r="246" fill="url(#vsBezel)" />
            <circle
              cx="250"
              cy="250"
              r="244.5"
              fill="none"
              stroke="#fff6d2"
              stroke-width="2"
              opacity=".5"
            />
            <circle
              cx="250"
              cy="250"
              r="237"
              fill="none"
              stroke="#4a3206"
              stroke-width="1.5"
              opacity=".5"
            />
            <g filter="url(#vsBulbGlow)">
              <circle
                v-for="bulb in wheelBulbs"
                :key="bulb.key"
                :cx="bulb.cx"
                :cy="bulb.cy"
                r="7.5"
                :fill="bulb.fill"
              />
            </g>
            <circle cx="250" cy="250" r="216" fill="url(#vsBezelIn)" />
            <circle cx="250" cy="250" r="209" fill="#0d0801" />
          </svg>

          <!-- Rotating layer: only the prize slices and their labels move. -->
          <svg
            class="wheel-layer wheel-layer--spin"
            :style="wheelStyle"
            viewBox="0 0 500 500"
            role="img"
            aria-label="วงล้อรางวัล"
          >
            <defs>
              <radialGradient id="vsSegA" cx="50%" cy="50%" r="72%">
                <stop offset="0%" stop-color="#b07d16" />
                <stop offset="55%" stop-color="#7d5710" />
                <stop offset="100%" stop-color="#4a3208" />
              </radialGradient>
              <radialGradient id="vsSegB" cx="50%" cy="50%" r="72%">
                <stop offset="0%" stop-color="#2e1f05" />
                <stop offset="55%" stop-color="#1c1203" />
                <stop offset="100%" stop-color="#0d0801" />
              </radialGradient>
              <radialGradient id="vsSegTop" cx="50%" cy="50%" r="72%">
                <stop offset="0%" stop-color="#f0433c" />
                <stop offset="55%" stop-color="#c4161d" />
                <stop offset="100%" stop-color="#6d0509" />
              </radialGradient>
              <linearGradient id="vsSep" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stop-color="#ffeaa8" stop-opacity=".12" />
                <stop offset="100%" stop-color="#ffeaa8" stop-opacity=".8" />
              </linearGradient>
              <radialGradient id="vsGloss" cx="50%" cy="-5%" r="88%">
                <stop offset="0%" stop-color="#fff" stop-opacity=".30" />
                <stop offset="42%" stop-color="#fff" stop-opacity=".08" />
                <stop offset="100%" stop-color="#fff" stop-opacity="0" />
              </radialGradient>
            </defs>
            <path
              v-for="seg in wheelSegments"
              :key="seg.key"
              :d="seg.d"
              :fill="seg.fill"
            />
            <line
              v-for="seg in wheelSegments"
              :key="`${seg.key}-sep`"
              x1="250"
              y1="250"
              :x2="seg.sepX"
              :y2="seg.sepY"
              stroke="url(#vsSep)"
              stroke-width="2.5"
            />
            <circle cx="250" cy="250" r="208" fill="url(#vsGloss)" />
            <circle
              cx="250"
              cy="250"
              r="208"
              fill="none"
              stroke="#ffeaa8"
              stroke-width="3.5"
            />
            <text
              v-for="label in wheelLabels"
              :key="label.key"
              :x="label.x"
              :y="label.y"
              :transform="`rotate(${label.rotate} ${label.x} ${label.y})`"
              text-anchor="middle"
              dominant-baseline="central"
              :class="['wheel__label', { 'wheel__label--top': label.top }]"
            >
              {{ label.text }}
            </text>
          </svg>

          <!-- Static hub, sits above the spinning layer. -->
          <svg class="wheel-layer" viewBox="0 0 500 500" aria-hidden="true">
            <defs>
              <radialGradient id="vsHub" cx="38%" cy="28%" r="82%">
                <stop offset="0%" stop-color="#fffbe9" />
                <stop offset="32%" stop-color="#f0c65a" />
                <stop offset="70%" stop-color="#a97d16" />
                <stop offset="100%" stop-color="#4a3208" />
              </radialGradient>
              <filter
                id="vsHubShadow"
                x="-60%"
                y="-60%"
                width="220%"
                height="220%"
              >
                <feDropShadow
                  dx="0"
                  dy="6"
                  stdDeviation="8"
                  flood-color="#000"
                  flood-opacity=".7"
                />
              </filter>
            </defs>
            <circle
              cx="250"
              cy="250"
              r="62"
              fill="#140d02"
              filter="url(#vsHubShadow)"
            />
            <circle cx="250" cy="250" r="52" fill="url(#vsHub)" />
            <circle
              cx="250"
              cy="250"
              r="40"
              fill="none"
              stroke="#6b4c08"
              stroke-width="1.8"
              opacity=".65"
            />
            <text
              x="250"
              y="242"
              text-anchor="middle"
              dominant-baseline="central"
              class="wheel__hub-a"
            >
              VIP
            </text>
            <text
              x="250"
              y="266"
              text-anchor="middle"
              dominant-baseline="central"
              class="wheel__hub-b"
            >
              GOLD
            </text>
          </svg>

          <svg
            class="wheel-pointer"
            width="88"
            height="112"
            viewBox="0 0 88 112"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="vsPtr" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#7d5a0d" />
                <stop offset="22%" stop-color="#ffefbe" />
                <stop offset="48%" stop-color="#f0c65a" />
                <stop offset="72%" stop-color="#b98c16" />
                <stop offset="100%" stop-color="#5c4109" />
              </linearGradient>
              <radialGradient id="vsGem" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stop-color="#fff" />
                <stop offset="40%" stop-color="#ff6b6b" />
                <stop offset="100%" stop-color="#a30d13" />
              </radialGradient>
            </defs>
            <path
              d="M44 108 L10 40 A34 34 0 1 1 78 40 Z"
              fill="url(#vsPtr)"
              stroke="#4a3206"
              stroke-width="3"
            />
            <circle cx="44" cy="36" r="20" fill="#2a1c04" />
            <circle cx="44" cy="36" r="15" fill="url(#vsGem)" />
            <ellipse cx="39" cy="29" rx="6" ry="4" fill="#fff" opacity=".55" />
          </svg>
        </div>

        <button v-if="canSpin" type="button" class="spin-button" @click="spin">
          <span>หมุนเลย</span>
          <small>ยอดฝากเดือน ส.ค. {{ formattedQualifyTotal }} บาท</small>
        </button>

        <p
          v-else-if="state === 'spinning'"
          class="spin-status"
          aria-live="polite"
        >
          กำลังหมุน... ลุ้นเลย!
        </p>

        <section
          v-else-if="card"
          class="status-card"
          :class="`status-card--${card.variant}`"
          aria-live="polite"
        >
          <h2 class="status-card__title">{{ card.title }}</h2>
          <p class="status-card__message">{{ card.message }}</p>
          <div class="status-card__actions">
            <button
              v-if="showRetry"
              type="button"
              class="vs-button"
              @click="loadStatus"
            >
              ตรวจสอบอีกครั้ง
            </button>
            <button
              type="button"
              class="vs-button vs-button--ghost"
              @click="closePage"
            >
              ปิดหน้าต่าง
            </button>
          </div>
        </section>

        <p class="fine-print">
          เฉพาะสมาชิกที่มียอดฝากรวมเดือนสิงหาคมตั้งแต่
          {{ formattedMinDeposit }} บาท • หมุนได้คนละ 1 ครั้ง
          (ต่อบัญชีและต่อเบอร์โทร) • เครดิตเข้าอัตโนมัติ
        </p>
      </template>
    </main>

    <footer class="vs-footer">UFAEASY888 • VIP GOLDEN SPIN</footer>
  </div>
</template>

<style>
body.vip-spin-body {
  min-height: 100vh;
  padding: 0 !important;
  margin: 0;
  background: #0b0702 !important;
  overscroll-behavior: none;
}
</style>

<style scoped>
.vip-spin-page {
  position: relative;
  display: flex;
  min-height: 100vh;
  overflow: hidden;
  color: #fff;
  flex-direction: column;
  background: radial-gradient(
      ellipse 60% 40% at 50% 30%,
      rgba(240, 198, 90, 0.18),
      transparent 70%
    ),
    linear-gradient(180deg, #160e02 0%, #0b0702 60%, #050301 100%);
  font-family: Kanit, "Sukhumvit Set", "Thonburi", sans-serif;
}
.vip-spin-page *,
.vip-spin-page *::before,
.vip-spin-page *::after {
  box-sizing: border-box;
}

.page-glow {
  position: fixed;
  width: 420px;
  height: 420px;
  pointer-events: none;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.2;
}
.page-glow--one {
  top: -230px;
  left: -170px;
  background: #f0c65a;
}
.page-glow--two {
  right: -230px;
  bottom: -170px;
  background: #b3131b;
}

.vs-header {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(100%, 560px);
  min-height: 68px;
  margin: 0 auto;
  padding: max(8px, env(safe-area-inset-top)) 18px 4px;
}
.vs-header__logo {
  width: 78px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 6px 14px rgba(240, 198, 90, 0.35));
}
.vs-header__avatar {
  width: 36px;
  height: 36px;
  border: 2px solid rgba(240, 198, 90, 0.75);
  border-radius: 50%;
  background: linear-gradient(135deg, #f0c65a, #4a3206);
  background-position: center;
  background-size: cover;
}

.vs-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  width: min(100% - 24px, 560px);
  margin: 0 auto;
  padding: 4px 0 22px;
  flex: 1;
  flex-direction: column;
}

.hero {
  margin: 0 0 10px;
  text-align: center;
}
.hero__badge {
  display: inline-block;
  padding: 4px 12px;
  color: #ffe9ac;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  border: 1px solid rgba(240, 198, 90, 0.4);
  border-radius: 999px;
  background: rgba(240, 198, 90, 0.12);
}
.hero__title {
  margin: 8px 0 0;
  color: #fff3d0;
  font-size: clamp(26px, 7.5vw, 36px);
  line-height: 1;
  font-weight: 1000;
  letter-spacing: -0.02em;
  text-shadow: 0 0 28px rgba(240, 198, 90, 0.35);
}
.hero__title em {
  color: #f0c65a;
  font-style: normal;
}
.hero__sub {
  margin: 7px 0 0;
  color: #d6c39a;
  font-size: 14px;
}

.wheel-wrap {
  position: relative;
  width: min(100%, 360px);
  margin: 8px 0 2px;
  aspect-ratio: 1 / 1;
}
.wheel-halo {
  position: absolute;
  inset: -6%;
  pointer-events: none;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(240, 198, 90, 0.3), transparent 62%);
  filter: blur(26px);
}
.wheel-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.wheel-layer--spin {
  filter: drop-shadow(0 16px 34px rgba(0, 0, 0, 0.6));
}
.wheel__label {
  fill: #ffeaa8;
  font-size: 38px;
  font-weight: 900;
  font-family: Kanit, "Sukhumvit Set", sans-serif;
  paint-order: stroke;
  stroke: #241703;
  stroke-width: 6px;
  stroke-linejoin: round;
}
.wheel__label--top {
  fill: #fff;
  font-size: 43px;
  stroke: #5c0206;
}
.wheel__hub-a {
  fill: #3a2705;
  font-size: 27px;
  font-weight: 900;
  font-family: Kanit, "Sukhumvit Set", sans-serif;
  letter-spacing: 2px;
}
.wheel__hub-b {
  fill: #5c4109;
  font-size: 14px;
  font-weight: 800;
  font-family: Kanit, "Sukhumvit Set", sans-serif;
  letter-spacing: 3px;
}
.wheel-pointer {
  position: absolute;
  top: -4%;
  left: 50%;
  z-index: 6;
  width: 13%;
  height: auto;
  transform: translateX(-50%);
  filter: drop-shadow(0 6px 11px rgba(0, 0, 0, 0.8));
}

.spin-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(100%, 380px);
  min-height: 62px;
  margin-top: 12px;
  padding: 9px 24px;
  color: #2a1c02;
  border: 0;
  border-radius: 999px;
  flex-direction: column;
  background: linear-gradient(135deg, #fff0bd, #f0c65a 55%, #c98f16);
  box-shadow: 0 14px 34px rgba(240, 198, 90, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  cursor: pointer;
}
.spin-button span {
  font-size: 21px;
  line-height: 1.1;
  font-weight: 1000;
}
.spin-button small {
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  opacity: 0.7;
}
.spin-button:active {
  transform: translateY(1px) scale(0.99);
}
.spin-status {
  margin: 16px 0 0;
  color: #ffe9ac;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
}

.loading-card,
.status-card {
  display: flex;
  align-items: center;
  width: min(100%, 420px);
  margin-top: 12px;
  padding: 22px 20px 18px;
  text-align: center;
  border: 1px solid rgba(240, 198, 90, 0.22);
  border-radius: 24px;
  flex-direction: column;
  background: linear-gradient(
    165deg,
    rgba(48, 32, 6, 0.96),
    rgba(11, 7, 2, 0.98)
  );
  box-shadow: 0 22px 62px rgba(0, 0, 0, 0.5);
}
.loading-card__spinner {
  width: 46px;
  height: 46px;
  margin-bottom: 14px;
  border: 4px solid rgba(255, 255, 255, 0.12);
  border-top-color: #f0c65a;
  border-radius: 50%;
  animation: vipSpinLoad 0.8s linear infinite;
}
.loading-card strong {
  color: #fff3d0;
  font-size: 17px;
}
.loading-card small {
  margin-top: 5px;
  color: #a8977a;
}

.status-card--success {
  border-color: rgba(75, 224, 138, 0.45);
}
.status-card--error {
  border-color: rgba(255, 123, 123, 0.45);
}
.status-card__title {
  margin: 0;
  color: #fff3d0;
  font-size: 22px;
  font-weight: 900;
}
.status-card--success .status-card__title {
  color: #b8ffcf;
}
.status-card__message {
  margin: 8px 0 0;
  color: #cbb994;
  font-size: 13.5px;
  line-height: 1.55;
}
.status-card__actions {
  display: flex;
  width: 100%;
  margin-top: 16px;
  gap: 9px;
  flex-direction: column;
}
.vs-button {
  width: 100%;
  min-height: 48px;
  padding: 10px 18px;
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  border: 1px solid rgba(240, 198, 90, 0.3);
  border-radius: 999px;
  background: rgba(240, 198, 90, 0.1);
  cursor: pointer;
}
.vs-button--ghost {
  min-height: 40px;
  color: #a8977a;
  font-size: 13px;
  font-weight: 600;
  border-color: transparent;
  background: transparent;
}

.fine-print {
  width: min(100%, 420px);
  margin: 12px 0 0;
  color: #7a6c52;
  font-size: 10.5px;
  line-height: 1.5;
  text-align: center;
}
.vs-footer {
  position: relative;
  z-index: 2;
  padding: 6px 12px max(12px, env(safe-area-inset-bottom));
  color: #574c39;
  font-size: 9px;
  text-align: center;
  letter-spacing: 0.2em;
}

@keyframes vipSpinLoad {
  to {
    transform: rotate(360deg);
  }
}
</style>
